import { useMemo } from "react";
import { StyleSheet, Text, View } from "react-native";

import { fontFamily, radius, textStyle } from "@passfirst/ui";

import { type Colors, useColors } from "../../theme";
import type { MasteryRow } from "../use-readiness";

interface Props {
  rows: MasteryRow[];
}

function clampPct(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, Math.round(n)));
}

// Recent-accuracy-by-topic strip. Each row renders `recentAccuracy`
// (the share of recent attempts answered correctly) — NOT the Mastery
// screen's theta-derived `masteryPct`, so it's labeled "recent
// accuracy" to keep the two numbers from reading as the same metric.
// Each row: name left + pct right + a thin progress bar tinted by
// severity (brick for <50%, amber for 50–79%, green for ≥80%). Hidden
// entirely when there are no attempts yet.
export function MasteryList({ rows }: Props): JSX.Element | null {
  const colors = useColors();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  if (rows.length === 0) return null;

  const colorForAccuracy = (pct: number): string => {
    if (pct < 50) return colors.error;
    if (pct < 80) return colors.caution;
    return colors.success;
  };

  return (
    <View style={styles.section}>
      <Text style={styles.eyebrow}>RECENT ACCURACY BY TOPIC</Text>
      <View style={styles.list}>
        {rows.map((row) => {
          const pct = clampPct((row.recentAccuracy ?? 0) * 100);
          return (
            <View key={row.topicSlug} style={styles.row}>
              <View style={styles.rowTop}>
                <Text style={styles.label}>{row.label}</Text>
                <Text style={styles.pct}>{pct}%</Text>
              </View>
              <View style={styles.track}>
                <View
                  style={[
                    styles.fill,
                    {
                      width: `${pct}%`,
                      backgroundColor: colorForAccuracy(pct),
                    },
                  ]}
                />
              </View>
            </View>
          );
        })}
      </View>
    </View>
  );
}

function makeStyles(colors: Colors) {
  return StyleSheet.create({
    section: { gap: 12 },
    eyebrow: {
      ...textStyle.eyebrow,
      color: colors.textMuted,
    },
    list: { gap: 12 },
    row: { gap: 6 },
    rowTop: {
      flexDirection: "row",
      justifyContent: "space-between",
    },
    label: {
      fontFamily: fontFamily.semiBold,
      fontSize: 14,
      color: colors.textPrimary,
    },
    pct: {
      fontFamily: fontFamily.monoMedium,
      fontSize: 12,
      color: colors.textMuted,
    },
    track: {
      height: 6,
      backgroundColor: colors.surfaceMuted,
      borderRadius: radius.pill,
      overflow: "hidden",
    },
    fill: { height: "100%", borderRadius: radius.pill },
  });
}
